import { useApolloClient } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";
import { GET_EMAIL_INCOMINGS_DIGEST } from "graphql/query/communication-gql";

type TCommunicationOrderByClause = {
  column: string;
  order: "ASC" | "DESC";
};

type TPaginatorData<TItem> = {
  paginatorInfo?: {
    total?: number | null;
  };
  data?: TItem[];
};

type TRawEmailIncomingDigest = {
  id?: string | null;
  received_at?: string | null;
  updated_at?: string | null;
};

type TUseCommunicationAutoRefreshParams = {
  autoRefreshPaused?: boolean;
  needsFullTaskSet: boolean;
  page: number;
  pageSize: number;
  text?: string;
  where?: unknown;
  orderBy: TCommunicationOrderByClause[];
  taskDigestSignature: string;
  serverTotal: number;
  scopeKey: string;
  refreshNow: () => Promise<void>;
  onNewEmailsApplied?: (count: number) => void;
};

const AUTO_REFRESH_INTERVAL_MS = 30000;
const AUTO_REFRESH_EVENT_THROTTLE_MS = 4000;

const buildIncomingDigestSignature = (
  incomings: TRawEmailIncomingDigest[] | undefined
): string =>
  (incomings || [])
    .map((incoming) => [
      incoming.id || "",
      incoming.received_at || "",
      incoming.updated_at || "",
    ].join(":"))
    .join("|");

export const useCommunicationAutoRefresh = ({
  autoRefreshPaused,
  needsFullTaskSet,
  page,
  pageSize,
  text,
  where,
  orderBy,
  taskDigestSignature,
  serverTotal,
  scopeKey,
  refreshNow,
  onNewEmailsApplied,
}: TUseCommunicationAutoRefreshParams) => {
  const client = useApolloClient();
  const backgroundRefreshRequestIdRef = useRef(0);
  const backgroundRefreshInFlightRef = useRef(false);
  const lastAutoRefreshEventAtRef = useRef(0);
  const [ hasPendingRefresh, setHasPendingRefresh ] = useState(false);
  const [ pendingRefreshCount, setPendingRefreshCount ] = useState(0);

  const applyPendingRefresh = useCallback(async (count = pendingRefreshCount) => {
    setHasPendingRefresh(false);
    setPendingRefreshCount(0);
    await refreshNow();

    if (count > 0) {
      onNewEmailsApplied?.(count);
    }
  }, [ onNewEmailsApplied, pendingRefreshCount, refreshNow ]);

  const checkForExternalUpdates = useCallback(async () => {
    if (needsFullTaskSet || backgroundRefreshInFlightRef.current) {
      return;
    }

    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      return;
    }

    if (autoRefreshPaused && hasPendingRefresh) {
      return;
    }

    const currentRequestId = ++backgroundRefreshRequestIdRef.current;
    const isStale = () => currentRequestId !== backgroundRefreshRequestIdRef.current;

    backgroundRefreshInFlightRef.current = true;

    try {
      const incomingDigestResponse = await client.query<{ emailIncomings: TPaginatorData<TRawEmailIncomingDigest> }>({
        query: GET_EMAIL_INCOMINGS_DIGEST,
        variables: {
          first: pageSize,
          page,
          text,
          where,
          orderBy,
        },
        fetchPolicy: "network-only",
      });

      if (isStale()) {
        return;
      }

      const nextDigestSignature = buildIncomingDigestSignature(
        incomingDigestResponse.data?.emailIncomings?.data
      );
      const nextServerTotal = incomingDigestResponse.data?.emailIncomings?.paginatorInfo?.total || 0;
      const didTasksChange = nextDigestSignature !== taskDigestSignature;
      const didTotalChange = nextServerTotal !== serverTotal;

      if (!didTasksChange && !didTotalChange) {
        return;
      }

      const nextPendingCount = Math.max(0, nextServerTotal - serverTotal);

      if (autoRefreshPaused) {
        setHasPendingRefresh(true);
        setPendingRefreshCount(nextPendingCount);
        return;
      }

      await applyPendingRefresh(nextPendingCount);
    } catch {
      // Keep the current board visible on silent refresh failures.
    } finally {
      if (!isStale()) {
        backgroundRefreshInFlightRef.current = false;
      }
    }
  }, [
    applyPendingRefresh,
    autoRefreshPaused,
    client,
    hasPendingRefresh,
    needsFullTaskSet,
    orderBy,
    page,
    pageSize,
    serverTotal,
    taskDigestSignature,
    text,
    where,
  ]);

  useEffect(() => {
    setHasPendingRefresh(false);
    setPendingRefreshCount(0);
  }, [ scopeKey ]);

  useEffect(() => {
    if (!hasPendingRefresh || autoRefreshPaused || needsFullTaskSet) {
      return;
    }

    void applyPendingRefresh();
  }, [ applyPendingRefresh, autoRefreshPaused, hasPendingRefresh, needsFullTaskSet ]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return () => {
        backgroundRefreshRequestIdRef.current += 1;
      };
    }

    if (needsFullTaskSet) {
      return () => {
        backgroundRefreshRequestIdRef.current += 1;
        backgroundRefreshInFlightRef.current = false;
      };
    }

    const triggerCheck = () => {
      void checkForExternalUpdates();
    };

    const triggerEventRefresh = () => {
      const now = Date.now();

      if (now - lastAutoRefreshEventAtRef.current < AUTO_REFRESH_EVENT_THROTTLE_MS) {
        return;
      }

      lastAutoRefreshEventAtRef.current = now;
      triggerCheck();
    };

    const intervalId =
      window.setInterval(triggerCheck, AUTO_REFRESH_INTERVAL_MS);
    const handleFocus = () => {
      triggerEventRefresh();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        triggerEventRefresh();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      backgroundRefreshRequestIdRef.current += 1;
      backgroundRefreshInFlightRef.current = false;
      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [ autoRefreshPaused, checkForExternalUpdates, needsFullTaskSet, scopeKey ]);

  return {
    hasPendingRefresh,
    pendingRefreshCount,
    applyPendingRefresh,
  };
};
